'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { Button } from '@/ui/button/button';

export interface MarkAsDropdownOption {
  value: string;
  label: string;
  icon?: LucideIcon;
  iconColor?: string;
}

export interface MarkAsDropdownProps {
  value?: string;
  options: MarkAsDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  displayOptions?: MarkAsDropdownOption[];
  variant?: 'default' | 'compact' | 'ghost' | 'icon';
  triggerIcon?: React.ReactNode;
  showChevron?: boolean;
  startIcon?: React.ReactNode;
  useSelectedOptionIcon?: boolean;
  /**
   * Si défini, le bouton affiche toujours ce texte fixe
   * au lieu de la valeur sélectionnée.
   * Utilisé pour Scoring et Rentabilité (Figma : label catégorie permanent).
   */
  forceLabel?: string;
  /**
   * Pour ces valeurs d’option, affiche `forceLabel` à la place du libellé
   * (ex. masquer « Aucun » et garder « Rentabilité » sur le bouton).
   */
  forceLabelWhenValueIn?: string[];
  /** Désactive le déclencheur et empêche l’ouverture du menu. */
  disabled?: boolean;
}

export const MarkAsDropdown: React.FC<MarkAsDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Marquer comme...',
  className = '',
  disabled = false,
  displayOptions,
  triggerIcon,
  showChevron = true,
  startIcon,
  useSelectedOptionIcon = true,
  variant = 'default',
  forceLabel,
  forceLabelWhenValueIn,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const optionsForDisplay = displayOptions ?? options;
  const selectedOption = optionsForDisplay.find((opt) => opt.value === value);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = buttonRect.bottom + 4;
    let left = buttonRect.left;

    if (top + dropdownRect.height > viewportHeight) {
      top = buttonRect.top - dropdownRect.height - 4;
    }

    if (left + dropdownRect.width > viewportWidth) {
      left = buttonRect.right - dropdownRect.width;
    }

    dropdownRef.current.style.top = `${top}px`;
    dropdownRef.current.style.left = `${left}px`;

    if (!triggerIcon) {
      // Le bouton peut être compact, mais le menu doit pouvoir s'élargir
      // pour afficher les options sans troncature.
      dropdownRef.current.style.width = 'max-content';
      dropdownRef.current.style.minWidth = `${buttonRect.width}px`;
      dropdownRef.current.style.maxWidth = '280px';
    } else {
      dropdownRef.current.style.minWidth = '200px';
    }
  }, [isOpen, triggerIcon]);

  // Label affiché dans le bouton :
  // 1. selectedOption?.label (si une valeur est choisie), sauf si on force le libellé catégorie
  // 2. forceLabel
  // 3. placeholder
  const useCategoryLabelOnly =
    Boolean(forceLabel) &&
    forceLabelWhenValueIn != null &&
    forceLabelWhenValueIn.length > 0 &&
    forceLabelWhenValueIn.includes(String(value ?? ''));

  const buttonLabel = useCategoryLabelOnly
    ? forceLabel!
    : selectedOption?.label ?? forceLabel ?? placeholder;

  const SelectedIcon = useSelectedOptionIcon ? selectedOption?.icon : undefined;
  const selectedIconColor = useSelectedOptionIcon ? selectedOption?.iconColor : undefined;

  const buttonStartIcon = triggerIcon
    ? null
    : startIcon
      ? startIcon
      : selectedIconColor
        ? <span className="mark-as-dropdown-label-dot" style={{ backgroundColor: selectedIconColor }} />
        : SelectedIcon
          ? <SelectedIcon size={16} className="mark-as-dropdown-label-icon" />
          : null;

  const buttonVariant = variant === 'ghost' || variant === 'icon' ? 'ghost' : 'secondary';
  const buttonSize = variant === 'compact' ? 'small' : 'medium';
  const triggerClassName = [
    'mark-as-dropdown-btn',
    'mark-as-dropdown-trigger',
    `mark-as-dropdown-trigger-${variant}`,
    `mark-as-dropdown-${variant}`,
    isOpen ? 'mark-as-dropdown-trigger-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`mark-as-dropdown-wrapper ${className}`} style={{ position: 'relative' }}>
      <Button
        ref={buttonRef}
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        disabled={disabled}
        iconOnly={variant === 'icon'}
        leftIcon={variant === 'icon' ? triggerIcon : buttonStartIcon}
        rightIcon={showChevron && !triggerIcon ? (
          <ChevronDown
            size={16}
            className={`mark-as-dropdown-chevron ${isOpen ? 'mark-as-dropdown-chevron-open' : ''}`}
          />
        ) : undefined}
        className={triggerClassName}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setIsOpen(!isOpen);
        }}
      >
        {variant === 'icon' ? undefined : (
          <span className="mark-as-dropdown-label">{triggerIcon ?? buttonLabel}</span>
        )}
      </Button>

      {isOpen && !disabled && (
        <>
          <div className="mark-as-dropdown-overlay" onClick={() => setIsOpen(false)} />
          <div ref={dropdownRef} className="mark-as-dropdown-menu">
            {options.map((option) => {
              const Icon = option.icon;
              const dot = option.iconColor ? (
                <span className="mark-as-dropdown-opt-dot" style={{ backgroundColor: option.iconColor }} />
              ) : null;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`mark-as-dropdown-option ${
                    value === option.value ? 'mark-as-dropdown-option-selected' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {dot || (Icon ? <Icon size={16} className="mark-as-dropdown-opt-icon" /> : null)}
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};